import React, { useId, useRef, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'usehooks-ts';
import _get from 'lodash/get';
import L, { LatLngExpression } from 'leaflet';
import Map from '@lib/components/Map/Map';
import { MapSize } from '@lib/components/Map/enums';
import { MapParams } from '@lib/components/Map/types';
import Typography from '@lib/components/Typography/Typography';
import Search from '@lib/components/Search/Search';
import { InputSizes } from '@lib/components/Input/enums';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggle from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle';
import { CheckboxSizes } from '@lib/components/CheckBox/enums';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import { handleCenterMap } from '@lib/utils/mapUtils';
import { SITES_MAP_WIDGET_VIEW_PARAMS } from '@lib/enums/localStorageKeys';
import SiteMapPopupContentAsyncWrap from 'components/SiteMapPopupContent/SiteMapPopupContentAsyncWrap';
import SectorMapPopupContentAsyncWrap from 'components/SectorMapPopupContent/SectorMapPopupContentAsyncWrap';
import NewBadge from '../../NewBadge';
import useSitesMapWidgetQueries from './hooks/useSitesMapWidgetQueries';
import styles from './SitesMapWidget.module.scss';

const TOGGLE_OPTIONS = [{ label: 'checked', value: 'checked' }];
const LABEL_TYPOGRAPHY_PROPS = { variant: 'caption' as const, strong: false };

export interface SitesMapWidgetProps {
  title?: string;
  showBadge?: boolean;
}

export default function SitesMapWidget({
  title,
  showBadge = false,
}: SitesMapWidgetProps) {
  const { t } = useTranslation();
  const uniqueId = useId();
  const mapRef = useRef<L.Map>(null);
  const [showSectors, setShowSectors] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mapParams, setMapParams] = useLocalStorage<MapParams | null>(
    SITES_MAP_WIDGET_VIEW_PARAMS,
    null,
  );

  const {
    mapData,
    siteLocationCenterQueryHookResult,
    siteLocationCenter,
    siteLocationCenterLoading,
    isLoading,
  } = useSitesMapWidgetQueries({
    mapParams,
    showSectors,
    searchQuery,
  });

  const refetchSiteLocationCenter = siteLocationCenterQueryHookResult.refetch;
  const onCenterMap = useCallback(() => {
    refetchSiteLocationCenter().then((res) => {
      handleCenterMap({ res, mapRef });
    });
  }, [refetchSiteLocationCenter]);

  const defaultCenter: LatLngExpression | null = useMemo(() => {
    if (siteLocationCenter?.lon && siteLocationCenter?.lat) {
      return { lng: siteLocationCenter.lon, lat: siteLocationCenter.lat };
    }
    return null;
  }, [siteLocationCenter]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const onToggleShowSectors = useCallback((value: boolean) => {
    setShowSectors(value);
  }, []);

  const popupContentRender = useCallback((renderProps: any) => {
    const entityId = _get(renderProps, ['properties', 'sourceables', 0, 'id']);
    const entityType = _get(renderProps, [
      'properties',
      'sourceables',
      0,
      'type',
    ]);

    if (entityType === 'SITE') {
      return (
        <SiteMapPopupContentAsyncWrap siteId={entityId} {...renderProps} />
      );
    }
    if (entityType === 'SECTOR') {
      return (
        <SectorMapPopupContentAsyncWrap sectorId={entityId} {...renderProps} />
      );
    }

    return undefined;
  }, []);

  const toggleOverlay = (
    <WhiteBox
      className={styles.toggleOverlay}
      verticalPaddings="custom"
      horizontalPaddings="custom"
    >
      <CheckBoxSingleBool
        name={`show-sectors-${uniqueId}`}
        value={showSectors}
        options={TOGGLE_OPTIONS}
        onChange={onToggleShowSectors}
        label={t('show-sectors')}
        CheckBoxComponent={CheckBoxAsToggle}
        labelTypographyProps={LABEL_TYPOGRAPHY_PROPS}
        size={CheckboxSizes.Small}
      />
    </WhiteBox>
  );

  // Don't render map until we have initial center or mapParams
  const shouldRenderMap = !siteLocationCenterLoading;

  return (
    <div className={styles.container}>
      {showBadge && <NewBadge className={styles.badge} />}
      <div className={styles.header}>
        <Typography variant="h3" className={styles.title}>
          {title || t('sites-map')}
        </Typography>
      </div>
      <div className={styles.searchWrapper}>
        <Search
          placeholder={t('sites-search-placeholder')}
          onChange={handleSearchChange}
          useSearchParam={false}
          useBottomMargin={false}
        />
      </div>
      <div className={styles.mapWrapper}>
        {!shouldRenderMap ? (
          <div className={styles.loaderWrapper}>
            <CircleLoader />
          </div>
        ) : (
          <CircleLoaderWrapper isLoading={isLoading}>
            <Map
              defaultCenter={defaultCenter}
              disableAutoFit
              mapParams={mapParams}
              mapRef={mapRef}
              onCenterMap={onCenterMap}
              PopupContent={popupContentRender}
              setMapParams={setMapParams}
              size={MapSize.Full}
              value={mapData}
              topRightOverlay={toggleOverlay}
            />
          </CircleLoaderWrapper>
        )}
      </div>
    </div>
  );
}
